import { Price, Programme, Workout } from '../../dao/core-dao'
import { AnalyticsEvent } from '../../service/analytics-events'
import { AnalyticsProperties, analyticsService } from '../../service/analytics-service'
import { coreService } from '../../service/core-service'
import { googleAnalyticsService } from '../../service/google-analytics-service'
import { messageService } from '../../service/message-service'
import { metaPixelService } from '../../service/meta-pixel-service'
import { cacheUtil } from '../../util/cache-util'
import { EventBusActionType, EventBusEntityType, EventBusMessage, eventBusUtil } from '../../util/event-bus-util'
import { logger } from '../../util/logger'
import { Subscription } from 'rxjs'

/**
 * Flat, id-based properties for analytics. The bus message used to be sent whole, which put the
 * entire `Workout` (description, equipment, trainers…) on every event as a nested blob nobody
 * could filter on. Ids and a title are what a dashboard needs.
 */
const workoutProps = (workout: Partial<Workout> | undefined, programme?: Partial<Programme>): AnalyticsProperties => ({
  workout_id: workout?.id,
  workout_title: workout?.title,
  trainer_ids: workout?.trainers?.map((trainer) => trainer.id),
  duration: workout?.duration,
  is_programme: !!programme,
  programme_id: programme?.id,
})

const programmeProps = (programme: Partial<Programme> | undefined): AnalyticsProperties => ({
  programme_id: programme?.id,
  programme_title: programme?.title,
})

const priceProps = (price: Price | undefined): AnalyticsProperties => ({
  product_id: price?.product.id,
  product_name: price?.product.name,
  price: price?.amount,
  currency: price?.currency,
  interval: price?.recurring?.interval,
  trial_duration_days: price?.product.trialDurationDays,
  source: 'stripe_checkout',
})

export class EventBusController {
  protected _subscriptions: Subscription[] = []

  protected _subscribe(
    params: { entities?: EventBusEntityType[]; actions?: EventBusActionType[] },
    cb: (eventMessage: EventBusMessage) => Promise<void>,
  ): void {
    this._subscriptions.push(eventBusUtil().subscribe(params, cb))
  }

  public unsubscribe(): void {
    this._subscriptions.forEach((s) => s.unsubscribe())
    this._subscriptions = []
  }

  public register(): void {
    this._subscriptionSet()
    this._subscribeToEmailNotifications()
    this._unsubscribeFromEmailNotifications()
    this._checkoutInitiated()
    this._paywallOpened()
    this._videoPlaybackStarted()
    this._videoPlaybackEnded()
    this._userCreated()
    this._registrationCompleted()
    this._programmeStarted()
    this._programmeCompleted()
    this._programmeCancelled()
    this._programmeVideoPlaybackStarted()
    this._programmeVideoPlaybackEnded()
  }

  public _subscriptionSet(): void {
    this._subscribe(
      {
        entities: [EventBusEntityType.SUBSCRIPTION],
        actions: [EventBusActionType.CREATE],
      },
      async (eventMessage: EventBusMessage) => {
        try {
          googleAnalyticsService.sendSubscriptionSet({ message: eventMessage }).catch((error) => logger.error(error))
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
        try {
          metaPixelService.sendSubscriptionSet({ message: eventMessage }).catch((error) => logger.error(error))
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
        this._reportPurchaseCompleted().catch((error) => logger.error(error))
      },
    )
  }

  /**
   * The Stripe return is the only browser-side signal a subscription now exists, and it says
   * nothing about the plan — so the plan and the trial flag come from the subscription itself,
   * the same lookup the Meta pixel's StartTrial/Purchase split already does.
   */
  protected async _reportPurchaseCompleted(): Promise<void> {
    const subscriptions = await coreService.getMyActiveOrTrialingSubscriptions()
    const subscription = subscriptions[0]
    const plan = subscription?.items[0]?.plan
    analyticsService.track(AnalyticsEvent.PURCHASE_COMPLETED, {
      product_id: plan?.id,
      price: plan?.amount,
      currency: plan?.currency,
      is_trial: subscription?.status === 'trialing',
      source: 'stripe_checkout',
    })
  }

  public _subscribeToEmailNotifications(): void {
    this._subscribe(
      {
        entities: [EventBusEntityType.MEMBERSHIP],
        actions: [EventBusActionType.CREATE],
      },
      async (_: EventBusMessage) => {
        try {
          messageService.subscribeToEmailNotifications()
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
      },
    )
  }

  public _unsubscribeFromEmailNotifications(): void {
    this._subscribe(
      {
        entities: [EventBusEntityType.MEMBERSHIP],
        actions: [EventBusActionType.REMOVE],
      },
      async (_: EventBusMessage) => {
        try {
          messageService.unsubscribeFromEmailNotifications()
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
      },
    )
  }

  protected _checkoutInitiated(): void {
    this._subscribe(
      {
        entities: [EventBusEntityType.CHECKOUT],
        actions: [EventBusActionType.CREATE],
      },
      async (eventMessage: EventBusMessage) => {
        try {
          googleAnalyticsService.sendCheckoutInitiated({ message: eventMessage })
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
        try {
          metaPixelService.sendCheckoutInitiated({ message: eventMessage })
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
        const { price }: { price?: Price } = eventMessage.meta ?? {}
        analyticsService.track(AnalyticsEvent.PURCHASE_STARTED, priceProps(price))
      },
    )
  }

  protected _paywallOpened(): void {
    this._subscribe(
      {
        entities: [EventBusEntityType.PAYWALL],
        actions: [EventBusActionType.CREATE],
      },
      async (eventMessage: EventBusMessage) => {
        // `meta` is the `PaywallContext` the trigger captured (`choose-plan-modal.tsx`); an
        // open from the profile's own "Choose plan" carries none, hence `app`.
        const { source, blockedWorkoutId, playedFeaturedWorkoutIds, daysSinceSignUp } = eventMessage.meta ?? {}
        analyticsService.track(AnalyticsEvent.PAYWALL_VIEWED, {
          source: source ?? 'app',
          workout_id: blockedWorkoutId,
          played_featured_ids: playedFeaturedWorkoutIds,
          played_featured_count: playedFeaturedWorkoutIds?.length,
          days_since_signup: daysSinceSignUp,
        })
      },
    )
  }

  protected _videoPlaybackStarted(): void {
    this._subscribe(
      {
        entities: [EventBusEntityType.VIDEO_PLAYBACK],
        actions: [EventBusActionType.CREATE],
      },
      async (eventMessage: EventBusMessage) => {
        try {
          googleAnalyticsService.sendVideoPlaybackStarted({ message: eventMessage })
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
        try {
          metaPixelService.sendVideoPlaybackStarted({ message: eventMessage })
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
        analyticsService.track(AnalyticsEvent.WORKOUT_STARTED, workoutProps(eventMessage.meta?.workout))
      },
    )
  }

  protected _videoPlaybackEnded(): void {
    this._subscribe(
      {
        entities: [EventBusEntityType.VIDEO_PLAYBACK],
        actions: [EventBusActionType.REMOVE],
      },
      async (eventMessage: EventBusMessage) => {
        try {
          googleAnalyticsService.sendVideoPlaybackEnded({ message: eventMessage })
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
        try {
          metaPixelService.sendVideoPlaybackEnded({ message: eventMessage })
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
        analyticsService.track(AnalyticsEvent.WORKOUT_COMPLETED, workoutProps(eventMessage.meta?.workout))
      },
    )
  }

  protected _userCreated(): void {
    this._subscribe(
      {
        entities: [EventBusEntityType.USER],
        actions: [EventBusActionType.CREATE],
      },
      async (eventMessage: EventBusMessage) => {
        try {
          googleAnalyticsService.sendUserCreated({ message: eventMessage })
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
      },
    )
  }

  protected _registrationCompleted(): void {
    this._subscribe(
      {
        entities: [EventBusEntityType.USER],
        actions: [EventBusActionType.EDIT],
      },
      async (eventMessage: EventBusMessage) => {
        try {
          googleAnalyticsService.sendRegistrationCompleted({ message: eventMessage })
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
      },
    )
  }

  protected _programmeStarted(): void {
    this._subscribe(
      {
        entities: [EventBusEntityType.PROGRAMME],
        actions: [EventBusActionType.CREATE],
      },
      async (eventMessage: EventBusMessage) => {
        analyticsService.track(AnalyticsEvent.PROGRAMME_STARTED, programmeProps(eventMessage.meta?.programme))
        try {
          googleAnalyticsService.sendProgrammeStarted({ message: eventMessage })
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
      },
    )
  }

  protected _programmeCompleted(): void {
    this._subscribe(
      {
        entities: [EventBusEntityType.PROGRAMME],
        actions: [EventBusActionType.REMOVE],
      },
      async (eventMessage: EventBusMessage) => {
        analyticsService.track(AnalyticsEvent.PROGRAMME_COMPLETED, programmeProps(eventMessage.meta?.programme))
        try {
          googleAnalyticsService.sendProgrammeCompleted({ message: eventMessage })
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
      },
    )
  }

  protected _programmeCancelled(): void {
    this._subscribe(
      {
        entities: [EventBusEntityType.PROGRAMME],
        actions: [EventBusActionType.CANCEL],
      },
      async (eventMessage: EventBusMessage) => {
        analyticsService.track(AnalyticsEvent.PROGRAMME_CANCELLED, programmeProps(eventMessage.meta?.programme))
        try {
          googleAnalyticsService.sendProgrammeCancelled({ message: eventMessage })
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
      },
    )
  }

  protected _programmeVideoPlaybackStarted(): void {
    this._subscribe(
      {
        entities: [EventBusEntityType.PROGRAMME_VIDEO_PLAYBACK],
        actions: [EventBusActionType.CREATE],
      },
      async (eventMessage: EventBusMessage) => {
        const { workout, programme } = eventMessage.meta ?? {}
        analyticsService.track(AnalyticsEvent.WORKOUT_STARTED, workoutProps(workout, programme))
        try {
          googleAnalyticsService.sendProgrammeWorkoutVideoStarted({ message: eventMessage })
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
      },
    )
  }

  protected _programmeVideoPlaybackEnded(): void {
    this._subscribe(
      {
        entities: [EventBusEntityType.PROGRAMME_VIDEO_PLAYBACK],
        actions: [EventBusActionType.REMOVE],
      },
      async (eventMessage: EventBusMessage) => {
        const { workout, programme } = eventMessage.meta ?? {}
        analyticsService.track(AnalyticsEvent.WORKOUT_COMPLETED, workoutProps(workout, programme))
        try {
          googleAnalyticsService.sendProgrammeWorkoutVideoFinished({ message: eventMessage })
        } catch (error) {
          logger.error(error instanceof Error ? error : String(error))
        }
      },
    )
  }
}

export const eventBusController = cacheUtil.singleton((): EventBusController => new EventBusController())
